// @flow
import EventEmitter from 'eventemitter3';

import LegacyDispatcher from "app/lib/legacyDispatcher";

import JobLogGroupStore from 'app/stores/JobLogGroupStore';
import JobLogLineStore from 'app/stores/JobLogLineStore';

const normalizeUrl = function(url) {
  const withoutAnchor = url.split("#")[0];

  // Normalize the /
  if (withoutAnchor[0] === "/") {
    return withoutAnchor;
  }

  return `/${withoutAnchor}`;
};

const navigateToUrl = function(url) {
  const currentUrl = normalizeUrl(window.location.pathname);
  const newUrl = normalizeUrl(url);

  // If the URL's are the same, just update the anchor, otherwise, navigate to
  // the new page.
  if (currentUrl === newUrl) {
    const newAnchor = url.split("#")[1];
    if (newAnchor != null) {
      return window.location.hash = newAnchor;
    }
  } else {
    return window.location = url;
  }
};

const navigateToAnchor = (anchor) => window.location.hash = anchor;

// If we make it blank, the page will jump to the top.
const clearAnchor = () => window.location.hash = '_';

// This is to hack around a problem with the browser where whem you load the page.
// - Page loads
// - I jump to the element in JS
// - Browser sees anchor, and doesn't find it, so jumps to the top
// This is a ugly hack to make it do the scroll after the initial anchor jump.
const scrollToElement = (el) => setTimeout(() => el.scrollIntoView(true), 250);

// Shortcut function to return url information.
const currentURL = () => ({
  anchor: (
    window.location.hash
      ? window.location.hash.slice(1)
      : null
  )
});

type Job = {
  id: string
};

class BuildPageStore {
  eventEmitter: EventEmitter;
  state: {
    expands: { [string]: boolean },
    line: ?{ [string]: { job: string, line: string } },
    newRetriesEnabled?: boolean
  };

  needsScrollingToLine: boolean;
  scrollToJobAfterRender: ?(boolean | Job);

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {
      expands: {},
      line: {}
    };

    this.needsScrollingToLine = false;
    this.scrollToJobAfterRender = null;

    LegacyDispatcher.on("page:ready", () => {
      const anchor = currentURL().anchor;

      this.scrollToJobAfterRender = false;
      this.needsScrollingToLine = false;

      if (anchor != null) {
        const parts = anchor.match(/([^/]+)\/?(\d+)?-?(\d+)?/);
        const job = parts[1];
        const group = parts[2];
        const line = parts[3];

        this.state.expands[job] = true;
        this.scrollToJobAfterRender = job;

        // Expand the group the line is in, also highlight the group
        if (group) {
          const groupId = `${job}/${group}`;

          JobLogGroupStore.setDefault(groupId, true);
          JobLogLineStore.highlight(groupId);

          // Highlight the line
          if (line) {
            const lineId = `${groupId}-${line}`;
            JobLogLineStore.highlight(lineId);
          }

          // We need to scroll to the line after it's been rendered
          this.needsScrollingToLine = true;
        }

        return this.eventEmitter.emit('change');
      }
    });

    LegacyDispatcher.on("job:toggle", ({ job }) => {
      const isExpanded = this.state.expands[job.id];

      if (isExpanded) {
        this.state.expands[job.id] = false;
        clearAnchor();
      } else {
        this.state.expands[job.id] = true;
        navigateToUrl(job.path);
      }

      return this.eventEmitter.emit('change');
    });

    LegacyDispatcher.on("job:focus", ({ job }) => {
      this.state.expands[job.id] = true;
      navigateToUrl(job.path);
      this.scrollToJobAfterRender = job.id;
      return this.eventEmitter.emit('change');
    });

    // Watch for when a job is rendered so we can jump to it if we need to
    // (unless we're still waiting for a highlight)
    LegacyDispatcher.on("job:expanded", ({ job, el }) => {
      if ((this.scrollToJobAfterRender === job.id) && !this.needsScrollingToLine) {
        scrollToElement(el);
        return this.scrollToJobAfterRender = false;
      }
    });

    // Show the new job when they click on the "Retried In" link
    LegacyDispatcher.on("job:retried-link-click", ({ job }) => {
      this.state.expands[job.id] = false;
      clearAnchor();
      this.state.expands[job.retriedInJobUuid] = true;
      navigateToUrl(window.location.pathname + `#${job.retriedInJobUuid}`);
      return this.eventEmitter.emit('change');
    });

    // Modify the current location anchor when a line is highlighted
    LegacyDispatcher.on("job_log_line:highlight", ({ lineId }) => {
      const [job, line] = lineId.split("/");

      // If we're turning off the current line
      if ((this.state.line != null) && (this.state.line.job === job) && (this.state.line.line === line)) {
        this.state.line = null;
        return navigateToAnchor(job);
      }

      this.state.line = { job, line };
      return navigateToAnchor(lineId);
    });

    // Watch for when a line is rendered highlighted so we can scroll to it if we should
    LegacyDispatcher.on("job_log_line:highlighted", ({ el }) => {
      if (this.needsScrollingToLine) {
        scrollToElement(el);
        this.scrollToJobAfterRender = null;
        return this.needsScrollingToLine = false;
      }
    });

    // Watch for when a group is rendered highlighted so we can scroll to it if we should
    // Pretty much the same as lines
    LegacyDispatcher.on("job_log_group:highlighted", ({ el }) => {
      if (this.needsScrollingToLine) {
        scrollToElement(el);
        this.scrollToJobAfterRender = null;
        return this.needsScrollingToLine = false;
      }
    });
  }

  addChangeListener(job: Job, callback: Function) {
    return this.eventEmitter.addListener('change', callback);
  }

  removeChangeListener(job: Job, callback: Function) {
    return this.eventEmitter.removeListener('change', callback);
  }

  setDefaultExpandedForJob(jobId: string, expanded: boolean) {
    // If the default is expanded, and their isn't something already in the
    // anchor, set the default epanded for this job.
    if (expanded) {
      if ((currentURL().anchor == null)) {
        this.state.expands[jobId] = true;
        return window.location.hash = jobId;
      }
    }
  }

  isJobExpanded(job: Job) {
    return this.state.expands[job.id] === true;
  }

  setNewRetriesEnabled(value: boolean) {
    return this.state.newRetriesEnabled = value;
  }

  isNewRetriesEnabled() {
    return this.state.newRetriesEnabled;
  }
}

export default new BuildPageStore();
